import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AnyBet, getCompletedBetsByPlayer, getTransactionsByPlayer, getVimplayTransactionsForPlayer } from '../../../../services/admin/BetsService.tsx';
import { formatNumbers, getBetFiguresDate, getBetLossAmount, getBetResultDelta, getBetWinAmount, isPropsBet } from '../../../../helpers/functions.tsx';
import { adminWallTimeToUtc, getAdminFiguresZonedParts } from '../../../../helpers/adminFiguresTime.tsx';
import {
    addPerformanceCalendarDays,
    formatPerformanceDateLabel,
    formatPerformanceDateTime,
    getPerformanceDailyPeriods,
    getPerformanceDailyRangeFromKey,
    getPerformanceDayStart,
    getPerformanceEventDateKey,
    getPerformanceEventMonthKey,
    getPerformanceEventWeekKey,
    getPerformanceEventYearKey,
    getPerformanceMonthlyPeriods,
    getPerformanceTimeZone,
    getPerformanceWeeklyPeriods,
    getPerformanceYearKey,
    getPerformanceYearlyPeriods,
} from '../../../../helpers/performanceFiguresTime.tsx';
import BetsDetails from '../../weekly-user/BetsDetails.tsx';
import { BetDetails } from '../../../../models/MyBetModel.tsx';
import { Timestamp } from 'firebase/firestore';
import { Transaction } from '../../../../models/User.tsx';
import { TransactionDetails } from '../../weekly-user/TransactionDetails.tsx';
import { CasinoTransactionDetails, groupCasinoPlays } from '../../weekly-user/CasinoTransactionDetails.tsx';
import { VimplayTransaction } from '../../../../models/VimplayTransaction.tsx';

type PerformanceData = {
    bets: AnyBet[];
    transactions: Transaction[];
    casino: VimplayTransaction[];
};

const casinoTotals = (rows: VimplayTransaction[]) => {
    let won = 0;
    let lost = 0;
    for (const play of groupCasinoPlays(rows)) {
        if (play.refunded) continue;
        if (play.net > 0) won += play.net;
        else if (play.net < 0) lost += Math.abs(play.net);
    }
    return { won, lost, net: won - lost };
};

const PERFORMANCE_SKELETON_ROWS = 10;
const PERFORMANCE_RECENT_DAYS = 90;
const PERFORMANCE_HISTORY_YEARS = 3;

const PerformanceTableSkeleton = () => (
    <>
        {Array.from({ length: PERFORMANCE_SKELETON_ROWS }).map((_, index) => (
            <tr key={index} className="user-transaction-skeleton-row" aria-hidden="true">
                <td className="mobile-bet-details">
                    <span className="rect-placeholder placeholder-performance-period" />
                </td>
                <td className="performance-lost">
                    <span className="rect-placeholder placeholder-transaction-amount" />
                </td>
                <td className="performance-won">
                    <span className="rect-placeholder placeholder-transaction-amount" />
                </td>
                <td>
                    <span className="rect-placeholder placeholder-transaction-amount" />
                </td>
            </tr>
        ))}
    </>
);

type DateValue = Timestamp | { seconds?: number; toDate?: () => Date } | null | undefined;
type PerformanceFilter = 'daily' | 'weekly' | 'monthly' | 'yearly';

type PerformanceRow = {
    key: string;
    period: string;
    won: number;
    lost: number;
    net: number;
};

const formatShortDate = (date: Date, timeZone: string) => {
    const { year, month, day } = getAdminFiguresZonedParts(date, timeZone);
    return `${month}/${day}/${String(year).slice(-2)}`;
};

const formatDailyLabel = (date: Date, timeZone: string) =>
    `${formatShortDate(date, timeZone)} ${formatPerformanceDateLabel(date, timeZone, { weekday: 'long' })}`;

const formatDailyLabelFromKey = (key: string, timeZone: string) => {
    const [year, month, day] = key.split('-').map(Number);
    const noon = adminWallTimeToUtc(year, month, day, 12, 0, 0, 0, timeZone);
    return formatDailyLabel(noon, timeZone);
};

const formatWeeklyLabel = (start: Date, end: Date, timeZone: string) =>
    `${formatShortDate(start, timeZone)} - ${formatShortDate(end, timeZone)}`;

const formatMonthlyLabel = (date: Date, timeZone: string) =>
    formatPerformanceDateLabel(date, timeZone, { month: 'long', year: 'numeric' });

const toDate = (timestamp: DateValue): Date => {
    if (!timestamp) return new Date(NaN);
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    if (timestamp.seconds != null) return new Date(timestamp.seconds * 1000);
    return new Date(timestamp as unknown as string | number);
};

function isParlayOrTeaser(player: BetDetails): boolean {
    return player.betType === 'Parlay' || player.betType === 'Teaser';
}

function isProps(player: BetDetails): player is BetDetails {
    return isPropsBet(player);
}

function buildPerformanceRows(
    filter: PerformanceFilter,
    formData: AnyBet[],
    allTransactions: Transaction[],
    casino: VimplayTransaction[],
): PerformanceRow[] {
    if (formData.length === 0 && allTransactions.length === 0 && casino.length === 0) return [];

    const now = new Date();
    const map: Record<string, PerformanceRow> = {};

    if (filter === 'daily') {
        for (const period of getPerformanceDailyPeriods(now, 28)) {
            map[period.key] = {
                key: period.key,
                period: formatDailyLabelFromKey(period.key, period.timeZone),
                won: 0,
                lost: 0,
                net: 0,
            };
        }
    }

    if (filter === 'weekly') {
        for (const period of getPerformanceWeeklyPeriods(now, 12)) {
            map[period.key] = {
                key: period.key,
                period: formatWeeklyLabel(period.start, period.end, period.timeZone),
                won: 0,
                lost: 0,
                net: 0,
            };
        }
    }

    if (filter === 'monthly') {
        for (const period of getPerformanceMonthlyPeriods(now, 12)) {
            map[period.key] = {
                key: period.key,
                period: formatMonthlyLabel(period.start, period.timeZone),
                won: 0,
                lost: 0,
                net: 0,
            };
        }
    }

    if (filter === 'yearly') {
        for (const period of getPerformanceYearlyPeriods(now, 3)) {
            map[period.key] = {
                key: period.key,
                period: period.key,
                won: 0,
                lost: 0,
                net: 0,
            };
        }
    }

    formData.forEach(bet => {
        const date = getBetFiguresDate(bet);
        if (!date || Number.isNaN(date.getTime())) return;

        let key = '';
        if (filter === 'daily') key = getPerformanceEventDateKey(date);
        if (filter === 'weekly') key = getPerformanceEventWeekKey(date);
        if (filter === 'monthly') key = getPerformanceEventMonthKey(date);
        if (filter === 'yearly') key = getPerformanceEventYearKey(date);
        if (!map[key]) return;

        const winAmount = getBetWinAmount(bet.reward ?? 0, bet.grader?.result);
        const lossAmount = getBetLossAmount(bet.bet ?? 0, bet.grader?.result);
        if (winAmount > 0) map[key].won += winAmount;
        if (lossAmount > 0) map[key].lost += lossAmount;
        map[key].net = map[key].won - map[key].lost;
    });

    allTransactions.forEach(tx => {
        if (!tx?.createDate) return;
        const date = toDate(tx.createDate);
        if (Number.isNaN(date.getTime())) return;

        let key = '';
        if (filter === 'daily') key = getPerformanceEventDateKey(date);
        if (filter === 'weekly') key = getPerformanceEventWeekKey(date);
        if (filter === 'monthly') key = getPerformanceEventMonthKey(date);
        if (filter === 'yearly') key = getPerformanceEventYearKey(date);
        if (!map[key]) return;

        if (tx.transactionType === 'CreditAdjustment') map[key].won += tx.amount ?? 0;
        if (tx.transactionType === 'DebitAdjustment') map[key].lost += tx.amount ?? 0;
        map[key].net = map[key].won - map[key].lost;
    });

    const casinoByKey: Record<string, VimplayTransaction[]> = {};
    casino.forEach(tx => {
        if (!tx?.createdAt) return;
        const date = toDate(tx.createdAt);
        if (Number.isNaN(date.getTime())) return;

        let key = '';
        if (filter === 'daily') key = getPerformanceEventDateKey(date);
        if (filter === 'weekly') key = getPerformanceEventWeekKey(date);
        if (filter === 'monthly') key = getPerformanceEventMonthKey(date);
        if (filter === 'yearly') key = getPerformanceEventYearKey(date);
        if (!map[key]) return;
        if (!casinoByKey[key]) casinoByKey[key] = [];
        casinoByKey[key].push(tx);
    });

    Object.entries(casinoByKey).forEach(([key, rows]) => {
        const totals = casinoTotals(rows);
        map[key].won += totals.won;
        map[key].lost += totals.lost;
        map[key].net = map[key].won - map[key].lost;
    });

    return Object.values(map);
}

const formatDateTime = (timestamp: DateValue): string => {
    const date = toDate(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return formatPerformanceDateTime(date);
};

const Performance = () => {
    const { user } = useParams<{ user: string }>();

    const [data, setData] = useState<PerformanceData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isBackfilling, setIsBackfilling] = useState(false);
    const [filter, setFilter] = useState<PerformanceFilter>('daily');
    const [details, setDetails] = useState<BetDetails[]>([]);
    const [detailsDates, setDetailsDates] = useState('');
    const [expandedPlayers, setExpandedPlayers] = useState<Set<string>>(() => new Set());
    const [selectedRow, setSelectedRow] = useState<string | null>(null);
    const [detailsTransactions, setDetailsTransactions] = useState<Transaction[]>([]);
    const [detailsCasino, setDetailsCasino] = useState<VimplayTransaction[]>([]);

    const bets = data?.bets;
    const transactions = data?.transactions;
    const casino = data?.casino;

    useEffect(() => {
        if (!user) return;

        let cancelled = false;
        setData(null);
        setIsLoading(true);
        setIsBackfilling(false);

        const now = new Date();
        const todayStart = getPerformanceDayStart(now, getPerformanceTimeZone(now));
        const recentSince = addPerformanceCalendarDays(todayStart, -PERFORMANCE_RECENT_DAYS, getPerformanceTimeZone(now));
        const currentYear = Number(getPerformanceYearKey(now, getPerformanceTimeZone(now)));
        const historySince = adminWallTimeToUtc(currentYear - (PERFORMANCE_HISTORY_YEARS - 1), 1, 1, 0, 0, 0, 0);

        const loadPerformance = async () => {
            try {
                const [recentBets, recentTransactions, recentCasino] = await Promise.all([
                    getCompletedBetsByPlayer(user, recentSince),
                    getTransactionsByPlayer(user, recentSince),
                    getVimplayTransactionsForPlayer({ userName: user }, recentSince),
                ]);
                if (cancelled) return;

                setData({
                    bets: recentBets.items ?? [],
                    transactions: recentTransactions.items ?? [],
                    casino: recentCasino,
                });
                setIsLoading(false);

                const alreadyComplete = recentBets.isFullHistory && recentTransactions.isFullHistory;
                if (alreadyComplete) return;

                setIsBackfilling(true);
                const [historyBets, historyTransactions, historyCasino] = await Promise.all([
                    getCompletedBetsByPlayer(user, historySince),
                    getTransactionsByPlayer(user, historySince),
                    getVimplayTransactionsForPlayer({ userName: user }, historySince),
                ]);
                if (cancelled) return;

                setData({
                    bets: historyBets.items ?? [],
                    transactions: historyTransactions.items ?? [],
                    casino: historyCasino,
                });
            } catch (error) {
                console.error('Error loading user performance:', error);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                    setIsBackfilling(false);
                }
            }
        };

        loadPerformance();

        return () => { cancelled = true; };
    }, [user]);

    const rows = useMemo(
        () => buildPerformanceRows(filter, bets ?? [], transactions ?? [], casino ?? []),
        [filter, bets, transactions, casino]
    );

    const handleRowClick = useCallback((row: PerformanceRow) => {
        if (filter !== 'daily') return;

        const { start: startOfDay, end: endOfDay } = getPerformanceDailyRangeFromKey(row.key);

        const dailyBets = (bets ?? []).filter(bet => {
            const betDate = getBetFiguresDate(bet);
            if (!betDate) return false;
            return betDate >= startOfDay && betDate <= endOfDay;
        });

        const dailyTransactions = (transactions ?? []).filter(tx => {
            if (!tx?.createDate) return false;
            const txDate = toDate(tx.createDate);
            return txDate >= startOfDay && txDate <= endOfDay;
        });

        const dailyCasino = (casino ?? []).filter(tx => {
            if (!tx?.createdAt) return false;
            const txDate = toDate(tx.createdAt);
            return txDate >= startOfDay && txDate <= endOfDay;
        });

        setDetails(dailyBets);
        setDetailsTransactions(dailyTransactions);
        setDetailsCasino(dailyCasino);
        setDetailsDates(row.period);
    }, [filter, bets, transactions, casino]);

    const togglePlayerDetails = useCallback((key: string) => {
        setExpandedPlayers(prev => {
            const updated = new Set(prev);
            if (updated.has(key)) updated.delete(key);
            else updated.add(key);
            return updated;
        });
    }, []);

    const getResult = useCallback((player: BetDetails) => getBetResultDelta(player), []);

    const detailSummary = useMemo(() => {
        if (details.length === 0 && detailsTransactions.length === 0 && detailsCasino.length === 0) return null;

        const totalBets = details.reduce((sum, bet) => sum + getBetResultDelta(bet), 0);

        const totalTransactions = detailsTransactions.reduce((sum, tx) => {
            if (tx.transactionType === 'CreditAdjustment') return sum + (tx.amount ?? 0);
            if (tx.transactionType === 'DebitAdjustment') return sum - (tx.amount ?? 0);
            return sum;
        }, 0);

        const totalResult = totalBets + totalTransactions + casinoTotals(detailsCasino).net;
        let resultClass = 'zero black-letters';
        if (totalResult > 0) resultClass = 'positive black-letters';
        else if (totalResult < 0) resultClass = 'negative black-letters';

        return { totalResult, resultClass };
    }, [details, detailsTransactions, detailsCasino]);

    return (
        <>
            <div style={{ paddingBottom: "100px" }}>
                <div className='container-transaction mt-1'>
                    <div className='table-responsive'>
                        {isLoading ? (
                            <span className="rect-placeholder placeholder-performance-filter" aria-hidden="true" />
                        ) : (
                            <select
                                id="select-filter"
                                name="selectfilter"
                                className="select-performance"
                                value={filter}
                                onChange={e => setFilter(e.target.value as PerformanceFilter)}
                            >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                                <option value="yearly">Yearly</option>
                            </select>
                        )}
                        {isBackfilling && (filter === 'monthly' || filter === 'yearly') && (
                            <span className="body-title" style={{ marginLeft: 12 }}>
                                Loading older history…
                            </span>
                        )}
                        <div className='table-scroll'>
                            <table className='table players-table'>
                                <thead className="header-customer">
                                    <tr>
                                        <th>
                                            <span className='header-size'>Period</span>
                                        </th>
                                        <th>
                                            <span className='header-size'>Won</span>
                                        </th>
                                        <th>
                                            <span className='header-size'>Lost</span>
                                        </th>
                                        <th>
                                            <span className='header-size'>Net</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="body-customer performance" aria-busy={isLoading}>
                                    {isLoading ? (
                                        <PerformanceTableSkeleton />
                                    ) : (
                                        <>
                                            {rows.map(row => {
                                                const isSelected = selectedRow === row.key;
                                                return (
                                                    <tr
                                                        key={row.key}
                                                        className={`row-hover ${isSelected ? 'row-selected' : ''}`}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => {
                                                            handleRowClick(row);
                                                            setSelectedRow(row.key);
                                                        }}
                                                    >
                                                        <td className='mobile-bet-details'>{row.period}</td>
                                                        <td className="text-success performance-won">${formatNumbers(row.won)}</td>
                                                        <td className="text-danger performance-lost">${formatNumbers(row.lost)}</td>
                                                        <td className={`performance-net ${row.won === 0 && row.lost === 0
                                                            ? '' : row.net > 0
                                                                ? 'text-success' : row.net < 0
                                                                    ? 'text-danger' : 'zero'
                                                        }`.trim()}>
                                                            ${formatNumbers(row.net)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {rows.length === 0 && (
                                                <tr>
                                                    <td colSpan={4} className="text-center">
                                                        No data
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {!isLoading && (
                    <div>
                        {detailSummary && (
                            <div className="bets-summary">
                                <span className='body-title padding'>
                                    Wagers: <span className='fw-bold padding'>{details.length}</span>
                                </span>
                                <span className='body-title'>
                                    Result:&nbsp;
                                    <span className={detailSummary.resultClass}>
                                        ${formatNumbers(detailSummary.totalResult)}
                                    </span>
                                </span>
                            </div>
                        )}

                        {filter === "daily" && (details.length > 0 || detailsTransactions.length > 0 || detailsCasino.length > 0) && (
                            <table className="table-bet mt-1">
                                <thead className='table-title-modal'>
                                    <tr>
                                        <th>{detailsDates}</th>
                                        <th></th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="body-customer customer-weekly">
                                    <BetsDetails
                                        details={details}
                                        isParlay={isParlayOrTeaser}
                                        togglePlayerDetails={togglePlayerDetails}
                                        getResult={getResult}
                                        expandedPlayers={expandedPlayers}
                                        formatDateTime={formatDateTime}
                                        isProps={isProps}
                                    />
                                    <TransactionDetails
                                        transactions={detailsTransactions}
                                        startIndex={details.length}
                                    />
                                    <CasinoTransactionDetails
                                        transactions={detailsCasino}
                                        startIndex={details.length + detailsTransactions.length}
                                    />
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default Performance;
